import TopAthlete from "./TopAthlete";
import Stack from "@mui/material/Stack";
import StatsBanner from "./StatsBanner";
import { useState, useEffect } from "react";
import { Theme } from "../../data/style/Theme";
import { useAuth } from "../../context/AuthContext";
import { useMutation } from "@tanstack/react-query";
import { getExerciseStatistics } from "../../api/exercises";
import {
  scrollBar,
  SpacedRow,
  SecondaryTitle,
  MainText,
} from "../../data/style/Style";

const ExerciseStats = ({ selectedExercise, exerciseType }) => {
  const { settings, profile } = useAuth();
  const [data, setData] = useState([]);

  const { mutate, isSuccess } = useMutation({
    mutationFn: getExerciseStatistics,
    onSuccess: (response) => setData(response.data),
  });

  useEffect(() => {
    let category = exerciseType;
    let exerciseId = selectedExercise.exercise;
    let score = selectedExercise.value;
    if (exerciseType === "cardio") {
      let distance = selectedExercise.distance;
      mutate({
        exerciseId,
        distance,
        category,
        score,
      });
    } else {
      mutate({
        exerciseId,
        category,
        score,
      });
    }
  }, [selectedExercise.exercise]);

  const percentages = [
    {
      label: "95%",
      value: (selectedExercise.value * 0.95).toFixed(2),
      value_lb: (parseFloat(selectedExercise.weight_lb) * 0.95).toFixed(2),
    },
    {
      label: "90%",
      value: (selectedExercise.value * 0.9).toFixed(2),
      value_lb: (parseFloat(selectedExercise.weight_lb) * 0.9).toFixed(2),
    },
    {
      label: "85%",
      value: (selectedExercise.value * 0.85).toFixed(2),
      value_lb: (parseFloat(selectedExercise.weight_lb) * 0.85).toFixed(2),
    },
    {
      label: "80%",
      value: (selectedExercise.value * 0.8).toFixed(2),
      value_lb: (parseFloat(selectedExercise.weight_lb) * 0.8).toFixed(2),
    },
    {
      label: "75%",
      value: (selectedExercise.value * 0.75).toFixed(2),
      value_lb: (parseFloat(selectedExercise.weight_lb) * 0.75).toFixed(2),
    },
    {
      label: "70%",
      value: (selectedExercise.value * 0.7).toFixed(2),
      value_lb: (parseFloat(selectedExercise.weight_lb) * 0.7).toFixed(2),
    },
    {
      label: "65%",
      value: (selectedExercise.value * 0.65).toFixed(2),
      value_lb: (parseFloat(selectedExercise.weight_lb) * 0.65).toFixed(2),
    },
    {
      label: "60%",
      value: (selectedExercise.value * 0.6).toFixed(2),
      value_lb: (parseFloat(selectedExercise.weight_lb) * 0.6).toFixed(2),
    },
    {
      label: "55%",
      value: (selectedExercise.value * 0.55).toFixed(2),
      value_lb: (parseFloat(selectedExercise.weight_lb) * 0.55).toFixed(2),
    },
    {
      label: "50%",
      value: (selectedExercise.value * 0.5).toFixed(2),
      value_lb: (parseFloat(selectedExercise.weight_lb) * 0.5).toFixed(2),
    },
  ];

  function getFlagEmoji(countryCode) {
    return countryCode
      .toUpperCase()
      .split("")
      .map((char) => String.fromCodePoint(127397 + char.charCodeAt()))
      .join("");
  }

  return (
    <Stack width={"100%"} gap={2}>
      {exerciseType === "weightlifting" && (
        <Stack gap={1}>
          <SecondaryTitle>Training Percentages</SecondaryTitle>
          <Stack
            gap={1}
            p={1}
            sx={{
              ...scrollBar,
              background: Theme.palette.gold + "07",
              borderRadius: 4,
            }}
            height={window.devicePixelRatio >= 2 ? "22vh" : "28vh"}
            overflow={"auto"}
          >
            {percentages.map((item) => (
              <SpacedRow sx={{ borderBottom: "2px solid #4d4c4c" }} pr={0.5}>
                <SecondaryTitle
                  sx={{
                    fontSize: "1rem !important",
                    color: Theme.palette.text + " !important",
                  }}
                >
                  {item.label}
                </SecondaryTitle>
                <Stack>
                  <MainText
                    sx={{ color: Theme.palette.gold + " !important" }}
                    fontWeight={700}
                  >
                    {settings.weight === 0 ? (
                      <>
                        {item.value}&nbsp;
                        <span
                          style={{
                            fontSize: "0.8rem",
                            color: Theme.palette.gold + "88",
                            fontFamily: "DM sans",
                          }}
                        >
                          kg
                        </span>
                      </>
                    ) : (
                      <>
                        {item.value_lb}&nbsp;{" "}
                        <span
                          style={{
                            fontSize: "0.8rem",
                            color: Theme.palette.muted,
                            fontFamily: "DM sans",
                          }}
                        >
                          lb
                        </span>
                      </>
                    )}
                  </MainText>
                </Stack>
              </SpacedRow>
            ))}
          </Stack>
        </Stack>
      )}
      <Stack gap={1}>
        <SecondaryTitle>Among athletes</SecondaryTitle>
        <SpacedRow gap={1}>
          <StatsBanner
            icon={"📊"}
            place={data.overall_place}
            text={"Overall"}
            color={
              exerciseType === "cardio"
                ? Theme.palette.up
                : exerciseType === "gymnastics"
                  ? Theme.palette.blue
                  : Theme.palette.gold
            }
          />
          <StatsBanner
            icon={"📍"}
            place={data.same_level_place}
            text={"Same level"}
            color={
              exerciseType === "cardio"
                ? Theme.palette.up
                : exerciseType === "gymnastics"
                  ? Theme.palette.blue
                  : Theme.palette.gold
            }
          />
          <StatsBanner
            icon={getFlagEmoji(profile.country)}
            place={data.same_country_place}
            text={"Same country"}
            color={
              exerciseType === "cardio"
                ? Theme.palette.up
                : exerciseType === "gymnastics"
                  ? Theme.palette.blue
                  : Theme.palette.gold
            }
          />
        </SpacedRow>
      </Stack>
      {isSuccess && (
        <Stack gap={1}>
          <SecondaryTitle>Leaderboard</SecondaryTitle>
          <TopAthlete
            athlete={data.overall_top_athlete}
            title={"🌍 World Best"}
            type={exerciseType}
          />
          <TopAthlete
            athlete={data.country_top_athlete}
            title={getFlagEmoji(profile?.country) + " Country Best"}
            type={exerciseType}
          />
        </Stack>
      )}
    </Stack>
  );
};

export default ExerciseStats;
